// KlantHistorySheet: rechter drawer met alle eerder verzonden Verfkeuze-mails per klant.
import { Loader2, Mail } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useCustomerHistory } from "@/hooks/useCustomerHistory";
import { TEMPLATE_LABEL } from "@/components/shared/klant-inlichten/templates";
import type { TemplateKey } from "@/components/shared/klant-inlichten/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerName: string;
  customerNumber: string | null;
  customerEmail: string | null;
}

const FMT = new Intl.DateTimeFormat("nl-NL", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const formatSentAt = (iso: string): string => {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return FMT.format(new Date(t)).replace(" om ", " ");
};

const KlantHistorySheet = ({
  open,
  onOpenChange,
  customerName,
  customerNumber,
  customerEmail,
}: Props) => {
  const { data, isLoading, error } = useCustomerHistory(customerNumber, customerEmail);

  const subLabel = customerNumber
    ? `Klantnummer ${customerNumber}`
    : customerEmail ?? "Geen klant-identifier";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Communicatie-historie van {customerName}</SheetTitle>
          <SheetDescription>{subLabel}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Laden...
            </div>
          ) : error ? (
            <div className="text-sm text-destructive py-8 text-center">
              Fout bij laden van historie
            </div>
          ) : !data || data.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              Geen eerdere mails verstuurd naar deze klant
            </div>
          ) : (
            <ol className="relative border-l border-border ml-2 space-y-4">
              {data.map((entry) => {
                const label =
                  TEMPLATE_LABEL[entry.template_key as TemplateKey] ?? entry.template_key;
                return (
                  <li key={entry.id} className="ml-4">
                    <span className="absolute -left-1.5 mt-1.5 flex h-3 w-3 items-center justify-center rounded-full bg-primary ring-4 ring-background">
                      <Mail className="h-2 w-2 text-primary-foreground" />
                    </span>
                    <div className="rounded-md border border-border bg-card p-3 shadow-sm">
                      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                        <span className="tabular-nums">{formatSentAt(entry.sent_at)}</span>
                        {entry.ordernumber && (
                          <span className="font-mono font-semibold text-foreground">
                            {entry.ordernumber}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-primary">
                        {label}
                      </div>
                      <div className="mt-1 text-sm font-medium text-foreground break-words">
                        {entry.subject}
                      </div>
                      {entry.sent_by && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          door {entry.sent_by}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default KlantHistorySheet;
